import type { ComponentType, CSSProperties, ReactNode } from "react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import TableClient from "@/components/tables/TableClient";
import { emailCampaignReportData } from "@/features/campaigns/email/utils/emailCampaignsDummyData";
import LearnMoreButton from "@/components/muiCustom/buttons/LearnMoreButton";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { getStatusCode } from "@/helpers/utils";

type EmailCampaignReportProps = {
  selectedLocation?: string | number | null;
};

type EmailCampaignReportRow = {
  id: number;
  name: string;
  status: string;
  delivered: string;
  unique_opens: string;
  unique_clicks: string;
};

type TableCellArgs = {
  row: {
    original: EmailCampaignReportRow;
  };
};

type TableColumn = {
  Header: string;
  accessor?: keyof EmailCampaignReportRow;
  canSort?: boolean;
  disableSortBy?: boolean;
  Cell?: (_args: TableCellArgs) => ReactNode;
};

type ReportLinkProps = {
  to: {
    pathname: string;
    state?: {
      selectedLocation: string | number | null;
    };
  };
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
};

const ReportLink = Link as ComponentType<ReportLinkProps>;

function EmailCampaignReport({ selectedLocation }: EmailCampaignReportProps) {
  const columns = useMemo(() => {
    return [
      {
        Header: "Campaign Name",
        accessor: "name",
        Cell: ({ row }: TableCellArgs) => {
          return (
            <ReportLink
              to={{
                pathname: `/email-campaign-report/detail/${row.original.id}`,
                state: {
                  selectedLocation: selectedLocation || null,
                },
              }}
              className="main-color link"
              style={{ fontWeight: 500 }}
            >
              {row.original.name}
            </ReportLink>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: TableCellArgs) => {
          return (
            <StatusChip
              label={row.original.status}
              status={getStatusCode(row.original.status)}
              showOnlyLabel
            />
          );
        },
      },
      { Header: "Delivered", accessor: "delivered" },
      { Header: "Unique Opens", accessor: "unique_opens" },
      { Header: "Unique Clicks", accessor: "unique_clicks" },
      {
        Header: "Actions",
        canSort: false,
        disableSortBy: true,
        Cell: ({ row }: TableCellArgs) => {
          return (
            <div className="d-flex align-items-center justify-content-end justify-content-lg-start">
              <LearnMoreButton className="mr-1" icon />
              {row.original.status !== "Sent" ? (
                <Link to={{}}>
                  <img
                    alt="Edit campaign"
                    src="/img/icons/edit.svg"
                    className="img-fluid ml-1"
                    style={{ width: 25 }}
                  />
                </Link>
              ) : null}
            </div>
          );
        },
      },
    ] satisfies TableColumn[];
  }, [selectedLocation]);

  return (
    <div className="mt-3 fw-sb">
      <TableClient
        columns={columns}
        data={emailCampaignReportData}
        filters={null}
        totalRecords={emailCampaignReportData?.length}
      />
    </div>
  );
}

export default EmailCampaignReport;
